"use client";

// One product card for the Locations products section. The WHOLE card is clickable via the
// stretched-link pattern: the article is `relative`, and the CTA link's `::after` overlay spans it.
// That keeps a single, properly-labelled link (no nested anchors) while the full card is the hit
// area. Owns hover/focus state so the reused 3D product visual (house / phone) tilts on interaction.

import { useState } from "react";
import Link from "next/link";
import { useReducedMotion } from "@/lib/motion";
import { ArrowRight, Check } from "@/components/icons";
import { ResidentialProxiesVisual } from "@/components/sections/home/products-overview/residential-proxies-visual";
import { MobileProxiesVisual } from "@/components/sections/home/products-overview/mobile-proxies-visual";

export interface ProductCardData {
  id: string;
  /** Which reused 3D product visual to render. */
  visual: "residential" | "mobile";
  title: string;
  description: string;
  points: string[];
  href: string;
  cta: string;
}

interface Props {
  product: ProductCardData;
}

export default function ProductCard({ product }: Props) {
  const reduced = useReducedMotion() ?? false;
  const [hovered, setHovered] = useState(false);
  const Visual = product.visual === "mobile" ? MobileProxiesVisual : ResidentialProxiesVisual;

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      className="group border-border bg-card hover:border-brand-green/40 has-[a:focus-visible]:border-brand-green/40 relative flex flex-col overflow-hidden rounded-2xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="border-border relative h-64 border-b">
        <Visual reduced={reduced} hovered={hovered} />
      </div>
      <div className="flex flex-1 flex-col p-6 sm:p-8">
        <h3 className="text-foreground mb-2 text-xl font-bold tracking-tight sm:text-2xl">
          {product.title}
        </h3>
        <p className="text-muted-foreground mb-5 leading-relaxed">{product.description}</p>
        <ul className="mb-6 flex flex-wrap gap-x-5 gap-y-2">
          {product.points.map((point) => (
            <li key={point} className="text-muted-foreground flex items-center gap-1.5 text-sm">
              <Check className="text-brand-green size-4 shrink-0" aria-hidden="true" />
              {point}
            </li>
          ))}
        </ul>
        <Link
          href={product.href}
          className="text-foreground mt-auto inline-flex items-center gap-1.5 text-sm font-semibold after:absolute after:inset-0 after:content-['']"
        >
          {product.cta}
          <ArrowRight className="text-brand-green size-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
    </article>
  );
}
